import React, { useState, useEffect } from "react";
import ModalLayout from "../components/layouts/ModalLayout";
import axios from "axios";
import { Download, FileText, FileSignature, FolderArchive, BarChart, Book, Search } from 'lucide-react';

type Props = {};

const Downloads = (props: Props) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [tab, setTab] = useState<number>(1)
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [targetModel, setTargetModel] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadSummary, setUploadSummary] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [type, setType] = useState<string>('')
  const [showBackendModal, setShowBackendModal] = useState<boolean>(false);
  const [file, setFile] = useState<FileList | null>(null)
  const [files, setFiles] = useState<any[]>([]);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [categoryCounts, setCategoryCounts] = useState<{ [key: string]: number }>({});
  const [currentPage, setCurrentPage] = useState(1);
  const filesPerPage = 8;


  const tabData = [
    { name: "All", tab: 1, icon: FolderArchive, key: "all" },
    { name: "Reports", tab: 2, icon: FileText, key: "reports" },
    { name: "Dashboard", tab: 3, icon: BarChart, key: "dashboard" },
    { name: "Eviction Resources", tab: 4, icon: Book, key: "evictionresources" },
  ];

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadStatus("idle");
    setUploadError(null);

    if (!file || file.length === 0) {
      setUploadError("Please select a PDF file.");
      setUploadStatus("error");
      return;
    }

    const selectedFile = file[0];

    if (selectedFile.type !== "application/pdf") {
      setUploadError("Only PDF files are allowed.");
      setUploadStatus("error");
      return;
    }

    if (!type) {
      setUploadError("Please select a file type.");
      setUploadStatus("error");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("type", type);

    setUploadStatus("uploading");

    try {
      const response = await fetch(`${BASE_URL}/upload-reports/`, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus("success");
        setFile(null);
        setType("");
        setShowAddModal(false); // Optional: auto-close
      } else {
        setUploadStatus("error");
        setUploadError(result.error || "Upload failed.");
      }
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus("error");
      setUploadError("Something went wrong. Please try again.");
    }
  };



  const uploadFileToBackend = async (file: File | null, type: string) => {
    if (!file) {
      alert("Please select a file.");
      return;
    }

    if (!targetModel) {
      alert("Please select a model to upload to.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", type);

    setIsUploading(true);
    setUploadProgress(0);
    setUploadSummary(null);

    try {
      const response = await axios.post(
        `https://somevictionsappprod.azurewebsites.net/api/upload-${targetModel}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // Cap at 90% during upload
              const percentCompleted = Math.min(
                Math.round((progressEvent.loaded * 90) / progressEvent.total),
                90
              );
              setUploadProgress(percentCompleted);
            }
          },
        }
      );

      // Complete to 100% when server responds
      setUploadProgress(100);

      const summary = response.data.message ||
        response.data.summary ||
        "Upload completed successfully";
      setUploadSummary(summary);

      // Close modal after 2 seconds to show success
      setTimeout(() => {
        setShowBackendModal(false);
      }, 2000);

    } catch (err: any) {
      let errorMessage = "Network or server error during upload.";

      if (err.response) {
        errorMessage = err.response.data.error ||
          err.response.data.message ||
          `Server error: ${err.response.status}`;
      } else if (err.request) {
        errorMessage = "No response received from server";
      }

      setUploadSummary(`Error: ${errorMessage}`);
      setUploadProgress(0); // Reset progress on error
      console.error("Upload failed:", err);
    } finally {
      setIsUploading(false);
    }
  };

  // Inside the component
  useEffect(() => {
    const fetchFiles = async () => {
      try {
        let url = "https://somevictionsappprod.azurewebsites.net/api/reports/";
        if (tab !== 1) {
          // Map tab values to categories
          const categoryMap: { [key: number]: string } = {
            2: "reports",
            3: "dashboard",
            4: "evictionresources",
            5: "Data Sets"
          };
          const selectedCategory = categoryMap[tab];
          url += `?category=${encodeURIComponent(selectedCategory)}`;
        }

        const response = await fetch(url);
        const data = await response.json();
        setFiles(data);
        // ✅ Always reset to page 1 on tab/category change
        setCurrentPage(1);
      } catch (error) {
        console.error("Failed to fetch files:", error);
      }
    };

    fetchFiles();
  }, [tab]);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        const res = await fetch("https://somevictionsappprod.azurewebsites.net/api/reports/");
        const allFiles = await res.json();

        const counts = allFiles.reduce((acc: { [key: string]: number }, file: any) => {
          const category = (file.category || "uncategorized").toLowerCase();
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});

        counts["all"] = allFiles.length;
        setCategoryCounts(counts);
      } catch (err) {
        console.error("Failed to fetch category counts:", err);
      }
    };

    fetchCategoryCounts();
  }, []);

  const indexOfLastFile = currentPage * filesPerPage;
  const indexOfFirstFile = indexOfLastFile - filesPerPage;
  const currentFiles = files.slice(indexOfFirstFile, indexOfLastFile);
  const totalPages = Math.ceil(files.length / filesPerPage);


  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold" style={{ color: "#1F2937" }}>
          Document Repository
        </h2>
        <p className="mt-1 text-sm" style={{ color: "#4B5563" }}>
          Access and download eviction-related documentation and resources.
        </p>
      </div>

      <div className="md:col-span-2 sm:col-span-1 mb-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {tabData.map(({ name, tab: tabIndex, icon: Icon, key }) => {
            const count = categoryCounts[key.toLowerCase()] || 0;
            const isActive = tab === tabIndex;

            return (
              <button
                key={name}
                onClick={() => setTab(tabIndex)}
                className={`group flex items-center p-4 text-left rounded-lg border transition-all cursor-pointer shadow-sm
            ${isActive
                    ? 'bg-[#F9FAFB] border-[#FB923C] ring-1 ring-[#FDBA74]'
                    : 'bg-white border-[#E5E7EB] hover:bg-[#F9FAFB] hover:border-[#FDBA74]'}
          `}
              >
                <div className="w-10 h-10 rounded-full bg-[#E5E7EB] group-hover:bg-[#FFFFFF] border border-[#F3F4F6] flex items-center justify-center mr-3 transition-colors">
                  <Icon
                    size={18}
                    className={`transition-colors ${isActive
                      ? 'text-[#FB923C]'
                      : 'text-[#6B7280] group-hover:text-[#FB923C]'
                      }`}
                  />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#111827]">
                    {name}
                  </h3>
                  <p className="text-xs font-semibold text-[#6B7280]">({count} files)</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="relative flex-1 max-w-md">
        <input
          type="text"
          placeholder="Search documents..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1] border-[#D1D5DB]"
        />
        <Search
          size={16}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#9CA3AF]"
        />
      </div>

      <div className="mt-6">
        {files.length === 0 ? (
          <p className="text-[#6B7280]">No files available.</p>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {currentFiles.map((file) => {
                const formattedDate = new Date(file.uploaded_at).toISOString().split("T")[0];
                return (
                  <div
                    key={file.id}
                    className="group relative bg-[#FFFFFF] rounded-lg shadow-md border border-[#F3F4F6] hover:shadow-lg transition-shadow duration-200 flex flex-col items-center text-center"
                  >
                    <div className="w-full aspect-[3/4] overflow-hidden rounded-t-md relative">
                      <img
                        src={file.thumbnail_url}
                        alt={`${file.name} thumbnail`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <a
                          href={file.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="opacity-0 group-hover:opacity-100 px-4 py-2 bg-white text-[#111827] rounded-lg shadow-lg text-sm font-medium transform translate-y-4 group-hover:translate-y-0 transition-all cursor-pointer flex items-center"
                        >
                          <Download size={16} className="mr-2" />
                          Download
                        </a>
                      </div>
                    </div>
                    <div className="p-4 w-full">
                      <h4 className="font-medium text-sm text-[#1F2937] truncate w-full text-left" title={file.name}>
                        {file.name}
                      </h4>
                      <div className="mt-2 flex items-center justify-between w-full text-xs text-[#6B7280]">
                        <div className="flex items-center">
                          <FileSignature size={14} className="text-[#4F46E5] mr-1" />
                          <span>{file.category}</span>
                        </div>
                        <span>{formattedDate}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-[#D1D5DB] rounded text-sm text-[#4B5563] hover:bg-[#F3F4F6] disabled:opacity-50"
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 border text-sm rounded ${currentPage === i + 1
                      ? "bg-[#EEF2FF] border-[#A5B4FC] text-[#1F2937]"
                      : "border-[#D1D5DB] text-[#4B5563] hover:bg-[#F3F4F6]"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 border border-[#D1D5DB] rounded text-sm text-[#4B5563] hover:bg-[#F3F4F6] disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>


      {showAddModal && <ModalLayout title="Add Download File">
        <div>
          <form className="mt-5" onSubmit={handleUpload}>
            <div className="mb-2 ">
              <label
                htmlFor="title"
                className="mb-1 block text-black dark:text-white text-sm"
              >
                File Type
              </label>
              <select
                id="type"
                name="type"
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
              >
                <option value=''>---Select---</option>
                <option value='reports'>Reports</option>
                <option value='dashboard'>Dashboards</option>
                <option value='evictionresources'>Eviction Resources</option>
              </select>
            </div>
            <div className="mb-2 ">
              <label
                htmlFor="file"
                className="mb-1 block text-black dark:text-white text-sm"
              >
                File (pdf)
              </label>
              <input
                type="file"
                id="file"
                name="file"
                accept="application/pdf" // Only allow PDF files
                onChange={(e) => setFile(e.target.files)}
                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="py-2 px-4 rounded text-white bg-danger" onClick={() => setShowAddModal(false)}>Close</button>
              <button type="submit" className="py-2 px-4 rounded text-white bg-primary" >Add</button>
              <button
                type="button"
                className="py-2 px-4 rounded text-white bg-primary"
                onClick={handleUpload}
              >
                Upload
              </button>
              <button
                type="button"
                className="py-2 px-4 rounded text-white bg-primary"
                onClick={async () => {
                  try {
                    const response = await fetch("https://somevictionsappprod.azurewebsites.net/api/partners/", {
                      method: "OPTIONS",
                    });
                    console.log("API responded with status:", response.status);
                  } catch (error) {
                    console.error("API test failed:", error);
                  }
                }}
              >
                Test API
              </button>
            </div>
          </form>
        </div>
        {uploadStatus === "uploading" && (
          <p className="text-sm text-blue-600 mb-2">Uploading file...</p>
        )}

        {uploadStatus === "success" && (
          <p className="text-sm text-green-600 mb-2">Upload successful!</p>
        )}

        {uploadStatus === "error" && uploadError && (
          <p className="text-sm text-red-600 mb-2">{uploadError}</p>
        )}
      </ModalLayout>}

      {showBackendModal && (
        <ModalLayout title="Upload File to Backend">
          <form className="mt-5" onSubmit={(e) => {
            e.preventDefault();
            uploadFileToBackend(file?.[0] || null, type);
          }}>
            {/* Model Selector */}
            <div className="mb-2">
              <label htmlFor="model" className="mb-1 block text-black dark:text-white text-sm">
                Select Model
              </label>
              <select
                id="model"
                name="model"
                value={targetModel}
                onChange={(e) => setTargetModel(e.target.value)}
                className="border-gray w-full text-black rounded border-[1.5px] border-stroke bg-transparent py-1 px-5 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
              >
                <option value="">---Select Model---</option>
                <option value="evictions">Evictions</option>
                <option value="preventions">Preventions</option>
                <option value="riskmap">Risk Map</option>
              </select>
            </div>

            {/* File Input */}
            <div className="mb-2">
              <label htmlFor="file" className="mb-1 block text-black dark:text-white text-sm">
                File (excel or csv)
              </label>
              <input
                type="file"
                id="file"
                name="file"
                onChange={(e) => setFile(e.target.files)}
                className="border-gray w-full text-black rounded border-stroke bg-transparent py-1 font-medium outline-none transition focus:border-primary active:border-primary dark:border-gray dark:bg-form-input dark:focus:border-primary"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2">
              <button type="button" className="py-2 px-4 rounded text-white bg-danger" onClick={() => setShowBackendModal(false)}>Close</button>
              <button type="submit" className="py-2 px-4 rounded text-white bg-primary">Upload</button>
            </div>
          </form>
          {isUploading && (
            <div className="mb-4">
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium">Uploading...</span>
                <span className="text-sm font-medium">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-[#e5e7eb] rounded-full h-2.5">
                <div
                  className="bg-[#2563eb] h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}

          {uploadSummary && (
            <div className={`mt-4 p-2 border rounded text-sm ${uploadSummary.startsWith('Error:')
              ? 'border-[#dc2626] bg-[#fee2e2] text-[#b91c1c]'
              : 'border-[#16a34a] bg-[#dcfce7] text-[#166534]'
              }`}>
              <strong>{uploadSummary.startsWith('Error:') ? 'Error:' : 'Success:'}</strong> {uploadSummary}
            </div>
          )}
        </ModalLayout>
      )}

    </div>
  )
};

export default Downloads;
